import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  collection, collectionGroup, query, where, getDocs,
  doc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import Wordmark from '../components/Wordmark';

// Once-per-session sessionStorage flag for the backfill below. Per-uid
// so signing out + back in as a different user re-runs the reconcile.
const SWM_BACKFILL_KEY_PREFIX = 'lists_swm_backfilled_';

/**
 * SharedWithMePage — full-page render of the user's `sharedWithMe`
 * collection. Reachable from the top board dropdown's "shared with
 * me" entry, and as the empty-state landing for users with 0 own
 * boards but >=1 shared item (App.js routes them here instead of
 * an unhelpful "loading...").
 *
 * Renders two sections:
 *   - Shared boards — clicking opens /board/{refId} (collectionGroup
 *     resolver in BoardPage handles the cross-user lookup).
 *   - Shared lists — clicking opens /list/{refId} (route added in a
 *     follow-up; for now we still render the entries with a
 *     "coming soon" cursor + tooltip rather than a dead link).
 *
 * Mirrors the site's dark monospace aesthetic. Empty state is
 * intentional — if the user has no shared content, this page just
 * tells them what they'd see here when someone shares with them.
 */
export default function SharedWithMePage({
  sharedWithMe,
  user,
  onSignOut,
  // Owned boards + new-board handler — used by the top-bar action so
  // users (especially share-recipients with no own boards yet) have a
  // way out of this page that doesn't depend on browser history.
  // navigate(-1) loops back here for users who arrived via the
  // empty-state /-route fallback.
  boards: ownBoards = [],
  onNewBoard,
}) {
  const navigate = useNavigate();
  const items = Array.isArray(sharedWithMe) ? sharedWithMe : [];
  const boards = items.filter(i => i.kind === 'board');
  const lists = items.filter(i => i.kind === 'list');

  // Backfill: for users invited via the old direct-arrayUnion path
  // (or anyone whose sharedWithMe doc was deleted by adminRevokeShare
  // and re-added later), the pointer doc is missing while the source
  // doc still has them in editors/viewers. Result is an empty
  // /shared-with-me page when the user clearly should have entries.
  // On mount, run a collectionGroup scan + write any missing pointers.
  // Once-per-session via sessionStorage so we only pay the read cost
  // once per tab. Idempotent — uses set+merge with the same shape
  // useResolvedBoard/List would write.
  useEffect(() => {
    if (!user?.uid) return;
    const sessionKey = SWM_BACKFILL_KEY_PREFIX + user.uid;
    try {
      if (sessionStorage.getItem(sessionKey)) return;
    } catch {} // sessionStorage can throw in embedded contexts
    let cancelled = false;
    (async () => {
      try {
        // Two pairs of queries — boards + lists, each with editors
        // and viewers. Run in parallel; we only need to find missing
        // pointer docs for any of them.
        const queries = [
          { coll: 'boards', field: 'editors', kind: 'board', role: 'editor' },
          { coll: 'boards', field: 'viewers', kind: 'board', role: 'viewer' },
          { coll: 'lists', field: 'editors', kind: 'list', role: 'editor' },
          { coll: 'lists', field: 'viewers', kind: 'list', role: 'viewer' },
        ];
        const snapshots = await Promise.all(queries.map(q =>
          getDocs(query(
            collectionGroup(db, q.coll),
            where(q.field, 'array-contains', user.uid),
          ))
        ));
        if (cancelled) return;

        // Existing pointers — keyed by refId.
        const existingSnap = await getDocs(collection(db, 'users', user.uid, 'sharedWithMe'));
        if (cancelled) return;
        const existingKeys = new Set(existingSnap.docs.map(d => d.id));

        // For each match, write a pointer if one doesn't exist.
        // Skip docs the user owns (path includes their own uid) —
        // those aren't shares, they're own content.
        const writes = [];
        for (let i = 0; i < queries.length; i++) {
          const { kind, role } = queries[i];
          const snap = snapshots[i];
          for (const d of snap.docs) {
            const refId = d.id;
            if (existingKeys.has(refId)) continue;
            const data = d.data();
            // Path walk:
            //   board: users/{ownerUid}/boards/{boardId}
            //   list:  users/{ownerUid}/boards/{boardId}/lists/{listId}
            const ownerUid = kind === 'list'
              ? d.ref.parent.parent.parent.parent.id
              : d.ref.parent.parent.id;
            if (ownerUid === user.uid) continue; // own content, not a share
            const boardId = kind === 'list'
              ? d.ref.parent.parent.id
              : refId;
            existingKeys.add(refId);
            writes.push(setDoc(
              doc(db, 'users', user.uid, 'sharedWithMe', refId),
              {
                kind,
                refOwnerUid: ownerUid,
                refId,
                refBoardId: boardId,
                role,
                cachedTitle: (kind === 'list' ? data.title : data.name) || '',
                addedAt: serverTimestamp(),
              },
              { merge: true },
            ));
          }
        }
        if (writes.length > 0) {
          await Promise.all(writes);
          if (cancelled) return;
          // Once writes land, the parent useSharedWithMe subscription
          // will fire with the new entries and the page re-renders.
        }
        try { sessionStorage.setItem(sessionKey, '1'); } catch {}
      } catch (err) {
        // Non-fatal — backfill is opportunistic, not required for
        // correctness. Forward path (addExistingUserToShare) writes
        // pointers at invite time anyway.
        console.warn('[SharedWithMePage] backfill failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Resolve refOwnerUid → display info for every item, in one batch
  // call. Pointer docs already carry `refOwnerUid` (the inviter / owner
  // of the shared content); some have a `cachedOwnerName` populated by
  // older code paths, but the cache is best-effort and goes stale on
  // rename. Resolving fresh on render is cheap (one batch call per
  // mount) and gives consistent "from {name}" subscripts.
  // Falls back to cachedOwnerName, then 'someone', if the resolve
  // misses the uid (e.g. account was hard-deleted). Stored as a
  // uid → { displayName, email } map.
  const [ownerDisplay, setOwnerDisplay] = useState({});
  // Stable dep: sort + join the uid set so React only re-runs the
  // effect when the actual set of owner uids changes, not on every
  // render that produces an equal array.
  const ownerUidsKey = items
    .map(i => i.refOwnerUid)
    .filter(Boolean)
    .sort()
    .join(',');
  useEffect(() => {
    if (!ownerUidsKey) return undefined;
    const uids = ownerUidsKey.split(',');
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'resolveUserDisplay');
        const res = await fn({ uids });
        if (cancelled) return;
        const map = {};
        for (const u of (res?.data?.users || [])) {
          map[u.uid] = { displayName: u.displayName || null, email: u.email || null };
        }
        setOwnerDisplay(prev => ({ ...prev, ...map }));
      } catch (err) {
        // Non-fatal — rows fall back to cachedOwnerName / 'someone'.
        console.warn('[SharedWithMePage] resolveUserDisplay failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [ownerUidsKey]);

  // Compose "from {primary}" hint for an item. Prefer freshly-resolved
  // displayName, then resolved email, then the cached owner name from
  // the pointer doc, then 'someone'. The last fallback is rare —
  // pointer docs are written either by acceptShareInvite (which knows
  // the inviter's email at minimum) or by useResolvedBoard/List (which
  // doesn't seed cachedOwnerName today). With resolveUserDisplay we
  // get it either way.
  const ownerHintFor = (item) => {
    const info = ownerDisplay[item.refOwnerUid] || {};
    const primary = info.displayName
      || info.email
      || item.cachedOwnerName
      || 'someone';
    return `from ${primary}`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      padding: '32px 24px',
      fontFamily: 'var(--mono)',
      color: 'var(--text)',
    }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        {/* Top bar with wordmark + back-to-app affordance */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px',
        }}>
          <Wordmark />
          {/* Top-right action depends on what the user has:
              - Owns boards → "← my boards" jumps to their first board.
              - No owned boards → "+ new board" so they have an exit
                that doesn't loop back here via navigate(-1).
              We deliberately avoid navigate(-1) — share-recipients
              who arrived via the empty-state redirect have no useful
              history entry and would loop. */}
          {ownBoards.length > 0 ? (
            <button
              onClick={() => navigate(`/board/${ownBoards[0].id}`)}
              style={topActionStyle}
              title="back to your boards"
            >
              ← my boards
            </button>
          ) : (
            <button
              onClick={() => onNewBoard?.()}
              style={topActionStyle}
              title="create your first board"
            >
              + new board
            </button>
          )}
        </div>

        <h1 style={{
          fontFamily: 'var(--mono)',
          fontSize: 'calc(18px * var(--ui-scale, 1))',
          fontWeight: 600,
          color: 'var(--text)',
          margin: '0 0 8px',
        }}>
          shared with me
        </h1>
        <p style={{
          fontSize: 'calc(12px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          margin: '0 0 28px',
        }}>
          {items.length === 0
            ? "you haven't been shared anything yet. when someone invites you to a board or list, it'll show up here."
            : `${items.length} item${items.length === 1 ? '' : 's'} shared with you.`}
        </p>

        {/* Boards section */}
        {boards.length > 0 && (
          <Section title="boards" count={boards.length}>
            {boards.map(b => (
              <SharedRow
                key={b.id}
                title={b.cachedTitle || '(untitled board)'}
                ownerHint={ownerHintFor(b)}
                role={b.role}
                kind="board"
                onClick={() => navigate(`/board/${b.refId}`)}
              />
            ))}
          </Section>
        )}

        {/* Lists section — per-list shares. Tapping opens the
            standalone /list/:id route (ListPage), which renders the
            single list as a focused editor. The recipient may not
            have access to the parent board, so we deliberately do
            NOT fall back to the board view here — that would 404 on
            list-only shares. */}
        {lists.length > 0 && (
          <Section title="lists" count={lists.length}>
            {lists.map(l => (
              <SharedRow
                key={l.id}
                title={l.cachedTitle || '(untitled list)'}
                ownerHint={ownerHintFor(l)}
                role={l.role}
                kind="list"
                onClick={() => navigate(`/list/${l.refId}`)}
              />
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

const topActionStyle = {
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text-dim)',
  padding: '6px 12px',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(11px * var(--ui-scale, 1))',
  cursor: 'pointer',
};

function Section({ title, count, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
        marginBottom: '8px',
      }}>
        <h2 style={{
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          margin: 0,
          fontWeight: 600,
        }}>
          {title}
        </h2>
        <span style={{
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
        }}>
          {count}
        </span>
      </div>
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
      }}>
        {children}
      </div>
    </div>
  );
}

function SharedRow({ title, ownerHint, role, kind, onClick, disabled, hint }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      title={hint || ''}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 14px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => {
        if (!disabled) e.currentTarget.style.background = 'var(--surface2)';
      }}
      onMouseLeave={e => {
        if (!disabled) e.currentTarget.style.background = 'var(--surface)';
      }}
    >
      <KindIcon kind={kind} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'calc(13px * var(--ui-scale, 1))',
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {title}
        </div>
        {ownerHint && (
          <div style={{
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text-dimmer)',
            marginTop: '2px',
          }}>
            {ownerHint}
          </div>
        )}
      </div>
      <RolePill role={role} />
    </div>
  );
}

function KindIcon({ kind }) {
  if (kind === 'list') {
    // Stack-of-lines glyph for a single list (matches the per-list
    // affordance language from the spec).
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--text-dim)' }}>
        <line x1="2" y1="3" x2="12" y2="3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <line x1="2" y1="7" x2="12" y2="7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <line x1="2" y1="11" x2="9" y2="11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    );
  }
  // Board-cluster glyph (4 small squares) — matches the existing
  // SharedWithMeSection icon language so users associate the symbol
  // with "a whole board" vs "a single list".
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--text-dim)' }}>
      <rect x="1" y="1" width="5" height="5" rx="0.7" stroke="currentColor" strokeWidth="1"/>
      <rect x="8" y="1" width="5" height="5" rx="0.7" stroke="currentColor" strokeWidth="1"/>
      <rect x="1" y="8" width="5" height="5" rx="0.7" stroke="currentColor" strokeWidth="1"/>
      <rect x="8" y="8" width="5" height="5" rx="0.7" stroke="currentColor" strokeWidth="1"/>
    </svg>
  );
}

function RolePill({ role }) {
  return (
    <span title={role === 'editor' ? 'can edit' : 'can view'} style={{
      flexShrink: 0,
      fontFamily: 'var(--mono)',
      fontSize: 'calc(9px * var(--ui-scale, 1))',
      color: 'var(--text-dimmer)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '2px 6px',
      letterSpacing: '0.4px',
      textTransform: 'uppercase',
    }}>
      {role === 'editor' ? 'editor' : 'viewer'}
    </span>
  );
}
